class ScrollHandler {
  /**
   * @param {Element} element
   * @param {{
   *  downSafeZone?: number
   *  upSafeZone?: number
   * }} options
   */
  constructor (element, { downSafeZone = 0, upSafeZone = 0 } = {}) {
    this.lastShouldBeVisible = this.shouldBeVisible
    this.lastIsInSafeZone = this.isInSafeZone

    this.isTicking = false
    this.lastKnownY = this.getY()
    this.offsetTrackedY = 0
    this.downSafeZone = downSafeZone
    this.upSafeZone = upSafeZone
    this.scrollOffset = 50
    /** @type {'up' | 'down'} */
    this.scrollDirection = undefined

    this.eventMap = {
      safeZoneChange: [],
      visibilityChange: [],
    }

    this.element = element

    this.updateEl = this.updateEl.bind(this)

    this.setupListeners()
  }

  setupListeners () {
    window.addEventListener('scroll', this.onScroll.bind(this), {
      passive: true,
    })
  }

  onScroll () {
    const previouslyKnown = this.lastKnownY
    const previousDirection = this.scrollDirection

    this.lastKnownY = window.scrollY
    this.scrollDirection = previouslyKnown < this.lastKnownY ? 'down' : 'up'

    if (previousDirection !== this.scrollDirection) {
      this.offsetTrackedY = this.lastKnownY
    }

    this.requestTick()
  }

  requestTick () {
    if (!this.isTicking) requestAnimationFrame(this.updateEl)

    this.isTicking = true
  }

  updateEl () {
    this.isTicking = false

    this.setVisibility()
  }

  setVisibility () {
    if (this.lastIsInSafeZone !== this.isInSafeZone) {
      this.lastIsInSafeZone = this.isInSafeZone

      this.emit('safeZoneChange', { safe: this.isInSafeZone })
    }

    if (this.lastShouldBeVisible !== this.shouldBeVisible) {
      this.lastShouldBeVisible = this.shouldBeVisible

      this.emit('visibilityChange', { visible: this.shouldBeVisible })
    }
  }

  getY () {
    return window.scrollY
  }

  /**
   * @param {keyof ScrollHandler['eventMap']} event
   * @param {(detail: { visible: boolean }) => void} callback
   */
  on (event, callback) {
    this.eventMap[event].push(callback)
  }

  /**
   * @param {keyof ScrollHandler['eventMap']} event
   * @param {unknown} detail
   */
  emit (event, detail) {
    this.eventMap[event].forEach(callback => callback(detail))
  }

  get shouldBeVisible () {
    if (this.isInSafeZone) {
      return true
    }

    if (this.scrollDirection === 'down') {
      return !this.isOverOffset
    } else if (this.scrollDirection === 'up') {
      return this.isOverOffset
    }
  }

  get isOverOffset () {
    if (this.scrollDirection === 'down') {
      return this.lastKnownY >= this.offsetTrackedY + this.scrollOffset
    } else if (this.scrollDirection === 'up') {
      return this.lastKnownY <= this.offsetTrackedY - this.scrollOffset
    }
  }

  get isInSafeZone () {
    const relevantSafeZone =
      this.scrollDirection === 'down' ? this.downSafeZone : this.upSafeZone

    return this.lastKnownY <= relevantSafeZone
  }
}

class CollectionToolbar extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.isSticky = false
  }

  setupListeners () {
    super.setupListeners()

    this.scrollHandler = new ScrollHandler(this, {
      downSafeZone: this.yPositions.bottom,
      upSafeZone: this.yPositions.top,
    })
    this.scrollHandler.on(
      'safeZoneChange',
      this.handleSafeZoneChange.bind(this),
    )
    this.scrollHandler.on(
      'visibilityChange',
      this.handleVisibilityChange.bind(this),
    )
  }

  /** @param {{ visible: boolean }} event */
  handleVisibilityChange ({ visible }) {
    this.toggleAttribute('shifted', visible)
  }

  /** @param {{ safe: boolean }} event */
  handleSafeZoneChange ({ safe }) {
    document.body.classList.toggle('lyt-Body-leftSafeZone', !safe)
  }

  get yPositions () {
    const boundingRect = this.getBoundingClientRect()

    return {
      top: boundingRect.top + window.scrollY - boundingRect.height,
      bottom: boundingRect.top + boundingRect.height + window.scrollY,
    }
  }
}

if (!window.customElements.get('collection-toolbar')) {
  window.customElements.define('collection-toolbar', CollectionToolbar)
}
